import { useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { CatalogAttendanceStatusesResponse, CatalogErrorTypesResponse } from "@mubin/contracts";
import { api, errorText, send } from "../lib/api";
import { Badge, Empty, ErrorNote, Loading, useSubmit } from "./ui";

/** A name that can be edited in place; saves on «حفظ». */
function NameCell({ value, onSave }: { value: string; onSave(name: string): Promise<unknown> }) {
  const [name, setName] = useState(value);
  const [busy, setBusy] = useState(false);
  const dirty = name.trim() !== value && name.trim().length >= 2;
  return (
    <div className="inline-edit">
      <input aria-label="الاسم" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} />
      {dirty && (
        <button
          className="btn btn-secondary btn-sm"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            void onSave(name.trim()).finally(() => setBusy(false));
          }}
        >
          حفظ
        </button>
      )}
    </div>
  );
}

/** Error types (B6): what the teacher marks while listening. Archived ones stay on past results. */
export function ErrorTypesCard({ organizationId }: { organizationId: string }) {
  const qc = useQueryClient();
  const key = ["catalog-error-types", organizationId];
  const list = useQuery({
    queryKey: key,
    queryFn: () => api<CatalogErrorTypesResponse>(`/v1/organizations/${organizationId}/error-types`),
  });
  const { busy, error, run } = useSubmit();
  const [name, setName] = useState("");
  const refresh = () => qc.invalidateQueries({ queryKey: key });
  const patch = (id: string, body: object) =>
    run(async () => {
      await send("PATCH", `/v1/error-types/${id}`, body);
      await refresh();
    }, errorText);
  function add(e: FormEvent) {
    e.preventDefault();
    void run(async () => {
      await send("POST", "/v1/error-types", { organizationId, name: name.trim() });
      setName("");
      await refresh();
    }, errorText);
  }

  return (
    <section className="card">
      <div className="card-h">
        <h2>أنواع الأخطاء</h2>
      </div>
      <div className="section">
        <p className="muted small">
          ما يسجله المعلم أثناء التسميع (مثل: خطأ حفظ، خطأ تجويد، تردد). المؤرشف لا يظهر للمعلمين ويبقى في
          النتائج السابقة.
        </p>
        <form className="inline-add" onSubmit={add}>
          <input
            aria-label="نوع خطأ جديد"
            placeholder="نوع خطأ جديد"
            value={name}
            maxLength={60}
            onChange={(e) => setName(e.target.value)}
          />
          <button className="btn btn-primary btn-sm" disabled={busy || name.trim().length < 2}>
            إضافة
          </button>
        </form>
        <ErrorNote error={error} />
      </div>
      {list.isPending ? (
        <Loading rows={2} />
      ) : list.isError ? (
        <Empty title="تعذر التحميل">{errorText(list.error)}</Empty>
      ) : list.data.errorTypes.length === 0 ? (
        <Empty title="لا أنواع أخطاء بعد">
          <p className="muted small">لن يستطيع المعلم تسجيل أخطاء التلاوة حتى تُضاف هنا.</p>
        </Empty>
      ) : (
        <div className="table-wrap">
          <table>
            <tbody>
              {list.data.errorTypes.map((t) => (
                <tr key={t.id}>
                  <td>
                    <NameCell key={t.name} value={t.name} onSave={(n) => patch(t.id, { name: n })} />
                  </td>
                  <td>{t.archived && <Badge tone="neutral">مؤرشف</Badge>}</td>
                  <td className="actions">
                    <button
                      className="btn btn-ghost btn-sm"
                      disabled={busy}
                      onClick={() => void patch(t.id, { archived: !t.archived })}
                    >
                      {t.archived ? "استعادة" : "أرشفة"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** Attendance states (B14) and whether each counts as present in the attendance rate. */
export function AttendanceStatusesCard({ organizationId }: { organizationId: string }) {
  const qc = useQueryClient();
  const key = ["catalog-attendance-statuses", organizationId];
  const list = useQuery({
    queryKey: key,
    queryFn: () =>
      api<CatalogAttendanceStatusesResponse>(`/v1/organizations/${organizationId}/attendance-statuses`),
  });
  const { busy, error, run } = useSubmit();
  const [name, setName] = useState("");
  const [present, setPresent] = useState(false);
  const refresh = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: key }),
      qc.invalidateQueries({ queryKey: ["attendance"] }),
    ]);
  const patch = (id: string, body: object) =>
    run(async () => {
      await send("PATCH", `/v1/attendance-statuses/${id}`, body);
      await refresh();
    }, errorText);
  function add(e: FormEvent) {
    e.preventDefault();
    void run(async () => {
      await send("POST", "/v1/attendance-statuses", {
        organizationId,
        name: name.trim(),
        countsAsPresent: present,
      });
      setName("");
      setPresent(false);
      await refresh();
    }, errorText);
  }

  return (
    <section className="card">
      <div className="card-h">
        <h2>حالات الحضور</h2>
      </div>
      <div className="section">
        <p className="muted small">
          «تُحسب حضورًا» تحدد أثرها في نسبة الحضور والتقارير. «حاضر» يسجله التسميع تلقائيًا فلا تُؤرشف.
        </p>
        <form className="inline-add" onSubmit={add}>
          <input
            aria-label="حالة جديدة"
            placeholder="حالة جديدة"
            value={name}
            maxLength={60}
            onChange={(e) => setName(e.target.value)}
          />
          <label className="check">
            <input type="checkbox" checked={present} onChange={(e) => setPresent(e.target.checked)} /> تُحسب
            حضورًا
          </label>
          <button className="btn btn-primary btn-sm" disabled={busy || name.trim().length < 2}>
            إضافة
          </button>
        </form>
        <ErrorNote error={error} />
      </div>
      {list.isPending ? (
        <Loading rows={3} />
      ) : list.isError ? (
        <Empty title="تعذر التحميل">{errorText(list.error)}</Empty>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>الحالة</th>
                <th>تُحسب حضورًا</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.data.statuses.map((s) => (
                <tr key={s.id}>
                  <td>
                    <NameCell key={s.name} value={s.name} onSave={(n) => patch(s.id, { name: n })} />{" "}
                    {s.archived && <Badge tone="neutral">مؤرشفة</Badge>}
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`${s.name} تُحسب حضورًا`}
                      checked={s.countsAsPresent}
                      disabled={busy || s.system}
                      onChange={(e) => void patch(s.id, { countsAsPresent: e.target.checked })}
                    />
                  </td>
                  <td className="actions">
                    {!s.system && (
                      <button
                        className="btn btn-ghost btn-sm"
                        disabled={busy}
                        onClick={() => void patch(s.id, { archived: !s.archived })}
                      >
                        {s.archived ? "استعادة" : "أرشفة"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
